import { Plugin, PluginKey } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { createFileLogger } from '../../../utils/logger';
import { getCitationData } from './LibraryDocsPlugin';
import useSpaceStore from '../../../stores/spaceStore';

const log = createFileLogger('CITATION_TOOLTIP');

const citationTooltipKey = new PluginKey('citationTooltip');

interface TooltipState {
  show: boolean;
  citationId: string | null;
  pos: { x: number; y: number } | null;
}

/**
 * Plugin that shows tooltip on citation hover and handles double-click
 */
export function citationTooltipPlugin(ydoc: any) {
  let tooltip: HTMLElement | null = null;
  let currentCitationId: string | null = null;
  let removeTimeout: NodeJS.Timeout | null = null;
  let isMouseOverTooltip = false;

  const createTooltip = (citationId: string, pos: { x: number; y: number }) => {
    // Cancel any pending removal
    if (removeTimeout) {
      clearTimeout(removeTimeout);
      removeTimeout = null;
    }

    // If tooltip already showing for this citation, don't recreate
    if (currentCitationId === citationId && tooltip) {
      return;
    }

    // Remove existing tooltip
    removeTooltip();

    // Get citation data from Y.js
    const citationData = getCitationData(ydoc, citationId);
    if (!citationData) {
      log.warn('[CitationTooltip] No data found for citation:', citationId);
      return;
    }

    // Create tooltip element
    tooltip = document.createElement('div');
    tooltip.className = 'citation-tooltip';
    tooltip.style.position = 'fixed';
    tooltip.style.zIndex = '10000';
    
    // Temporarily append to measure height
    tooltip.style.visibility = 'hidden';
    document.body.appendChild(tooltip);

    // Build tooltip content
    const references = citationData.references || [];
    const content = references.map((ref: any) => {
      const authors = ref.authors || [];
      const maxAuthors = 5;
      const authorsText = authors.length > maxAuthors
        ? `${authors.slice(0, maxAuthors).join(', ')} et al.`
        : authors.join(', ') || 'Unknown authors';
      
      return `
        <div class="citation-tooltip__ref">
          <div class="citation-tooltip__title">${ref.title || 'Untitled'}</div>
          <div class="citation-tooltip__authors">
            ${authorsText}
          </div>
          ${ref.journal ? `<div class="citation-tooltip__journal">${ref.journal}</div>` : ''}
          <div class="citation-tooltip__year">${ref.year || 'n.d.'}</div>
          ${ref.primaryPdfId ? '<div class="citation-tooltip__hint">Double-click to open PDF</div>' : ''}
        </div>
      `;
    }).join('');

    tooltip.innerHTML = content;
    
    // Measure tooltip height
    const tooltipHeight = tooltip.offsetHeight;
    const tooltipWidth = tooltip.offsetWidth;
    
    // Position tooltip above the citation with small gap
    let x = pos.x;
    let y = pos.y - 4; // 4px gap above citation
    
    // Adjust if tooltip goes off left edge
    if (x < 16) {
      x = 16;
    }
    
    // Adjust if tooltip goes off right edge
    if (x + tooltipWidth > window.innerWidth - 16) {
      x = window.innerWidth - tooltipWidth - 16;
    }
    
    // If tooltip goes off top edge, show below instead
    if (y < 16) {
      y = pos.y + 20; // Show below citation with small gap
    }
    
    tooltip.style.left = `${x}px`;
    tooltip.style.top = `${y}px`;
    tooltip.style.visibility = 'visible';
    
    currentCitationId = citationId;

    // Add mouse events to tooltip itself
    tooltip.addEventListener('mouseenter', () => {
      isMouseOverTooltip = true;
      if (removeTimeout) {
        clearTimeout(removeTimeout);
        removeTimeout = null;
      }
    });

    tooltip.addEventListener('mouseleave', () => {
      isMouseOverTooltip = false;
      scheduleRemoval();
    });

    log.info('[CitationTooltip] Tooltip shown:', citationId);
  };

  const scheduleRemoval = () => {
    if (removeTimeout) {
      clearTimeout(removeTimeout);
    }
    removeTimeout = setTimeout(() => {
      if (!isMouseOverTooltip) {
        removeTooltip();
      }
    }, 200);
  };

  const removeTooltip = () => {
    if (tooltip && tooltip.parentNode) {
      tooltip.parentNode.removeChild(tooltip);
      tooltip = null;
      currentCitationId = null;
    }
    if (removeTimeout) {
      clearTimeout(removeTimeout);
      removeTimeout = null;
    }
  };

  const handleDoubleClick = async (citationId: string) => {
    const citationData = getCitationData(ydoc, citationId);
    if (!citationData) return;

    // Get first reference's primary PDF
    const firstRef = citationData.references?.[0];
    if (!firstRef?.primaryPdfId) return;
    
    log.info('[CitationTooltip] Opening PDF:', firstRef.primaryPdfId);
    
    try {
      const { actions, windows } = useSpaceStore.getState();
      const entityId = firstRef.primaryPdfId;
      
      // Check if PDF is already open
      const existingWindow = Object.values(windows).find(window => {
        const windowEntity = useSpaceStore.getState().getWindowEntity(window.id);
        if (!windowEntity) return false;
        const windowEntityId = windowEntity._id || windowEntity.id || windowEntity.llmId;
        return windowEntityId === entityId || windowEntity.llmId === entityId;
      });
      
      if (existingWindow) {
        // Already open - just focus it
        actions.setActiveWindow(existingWindow.id);
      } else {
        // Fetch entity and open in new window
        const { entityService } = await import('../../../services/entityService');
        const entity = await entityService.get(entityId);
        
        if (entity) {
          const { getAppTypeForEntity } = await import('../../../utils/entityTypeMapping');
          const appType = getAppTypeForEntity(entity);
          const windowId = await actions.createWindow({
            appType,
            entity,
            position: { x: 200, y: 100 },
            size: { width: 800, height: 900 }
          });
          actions.setActiveWindow(windowId);
        }
      }
    } catch (error) {
      log.error('[CitationTooltip] Failed to open PDF:', error);
    }
  };

  return new Plugin({
    key: citationTooltipKey,
    
    props: {
      handleDOMEvents: {
        mousemove: (view: EditorView, event: MouseEvent) => {
          const target = event.target as HTMLElement;
          
          // Check if hovering over citation mark
          const citationSpan = target.closest('span[data-citation-id]') as HTMLElement;
          
          if (citationSpan) {
            const citationId = citationSpan.getAttribute('data-citation-id');
            
            if (citationId) {
              const rect = citationSpan.getBoundingClientRect();
              createTooltip(citationId, {
                x: rect.left,
                y: rect.top
              });
            }
          } else if (tooltip && !target.closest('.citation-tooltip')) {
            // Mouse left citation and not over tooltip - schedule removal
            scheduleRemoval();
          }
          
          return false;
        },
        
        mouseleave: () => {
          // Remove tooltip when mouse leaves editor
          removeTooltip();
          return false;
        },
        
        dblclick: (view: EditorView, event: MouseEvent) => {
          const target = event.target as HTMLElement;
          const citationSpan = target.closest('span[data-citation-id]') as HTMLElement;
          
          if (citationSpan) {
            const citationId = citationSpan.getAttribute('data-citation-id');
            if (citationId) {
              handleDoubleClick(citationId);
              event.preventDefault();
              return true;
            }
          }
          
          return false;
        }
      }
    }
  });
}

export { citationTooltipKey };

